import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { SEOHead } from './SEOHead';
import { WebAppSchema, FAQSchema, BreadcrumbSchema } from './StructuredData';
import { seoData, getRelatedToolsData } from './seoData';

/**
 * Adds SEO meta tags, H1 heading, content section, FAQ, related tools, and structured data.
 * Provides full on-page SEO for tool pages including proper heading hierarchy (H1 > H2 > H3).
 */
export const ToolPageExtras = ({ toolId }) => {
  const data = seoData[toolId];
  if (!data) return null;

  const relatedTools = getRelatedToolsData(toolId);
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: data.title.split(' - ')[0].replace('Free ', ''), url: data.path },
  ];

  const toolName = data.title.split(' - ')[0].replace('Free ', '');

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead
        title={data.title}
        description={data.description}
        keywords={data.keywords}
        path={data.path}
      />

      {/* Structured Data */}
      <WebAppSchema
        name={toolName}
        description={data.description}
        url={data.path}
        category="UtilityApplication"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      {data.faqs && <FAQSchema faqs={data.faqs} />}

      {/* SEO Content Section - Adds text content for better text-to-code ratio */}
      <section
        aria-labelledby={`seo-content-${toolId}`}
        style={{
          marginTop: '48px',
          maxWidth: '800px',
          marginLeft: 'auto',
          marginRight: 'auto',
          padding: '32px',
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid var(--border-color)',
        }}
      >
        <h2
          id={`seo-content-${toolId}`}
          style={{
            fontSize: '1.4rem',
            fontWeight: 800,
            color: 'var(--text-primary)',
            marginBottom: '12px',
            marginTop: 0,
          }}
        >
          About {toolName}
        </h2>
        <p
          style={{
            fontSize: '0.95rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.7,
            margin: '0 0 16px 0',
          }}
        >
          {data.description} This tool is part of the Toolmetrico suite of free online utilities
          designed for developers, designers, and content creators. All processing happens entirely
          in your browser — your files and data never leave your device, ensuring complete privacy
          and security.
        </p>

        <h3
          style={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            marginBottom: '8px',
            marginTop: '20px',
          }}
        >
          Key Features
        </h3>
        <ul
          style={{
            fontSize: '0.9rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
            paddingLeft: '20px',
            margin: 0,
          }}
        >
          <li>100% client-side processing — no server uploads</li>
          <li>Free to use with no registration or signup required</li>
          <li>Works on desktop, tablet, and mobile devices</li>
          <li>Fast performance with instant results</li>
          <li>No watermarks or usage limits</li>
        </ul>
      </section>

      {/* FAQ Section */}
      {data.faqs && data.faqs.length > 0 && (
        <section
          aria-labelledby={`faq-heading-${toolId}`}
          style={{
            marginTop: '48px',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <h2
            id={`faq-heading-${toolId}`}
            style={{
              fontSize: '1.4rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              marginBottom: '24px',
              textAlign: 'center',
            }}
          >
            Frequently Asked Questions
          </h2>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {data.faqs.map((faq, index) => (
              <details
                key={index}
                style={{
                  background: '#ffffff',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '20px 24px',
                  cursor: 'pointer',
                }}
              >
                <summary
                  style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    listStyle: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>{faq.question}</span>
                  <ChevronRight
                    size={16}
                    style={{
                      color: 'var(--text-muted)',
                      transition: 'transform 0.2s',
                      flexShrink: 0,
                      marginLeft: '12px',
                    }}
                    className="faq-chevron-icon"
                  />
                </summary>
                <p
                  style={{
                    fontSize: '0.92rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.7,
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--border-color)',
                  }}
                >
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Related Tools Section (Internal Linking) */}
      {relatedTools.length > 0 && (
        <section
          aria-labelledby={`related-tools-heading-${toolId}`}
          style={{
            marginTop: '48px',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <h2
            id={`related-tools-heading-${toolId}`}
            style={{
              fontSize: '1.3rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              marginBottom: '20px',
              textAlign: 'center',
            }}
          >
            Related Tools You Might Like
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px',
            }}
          >
            {relatedTools.map((tool) => (
              <Link
                key={tool.id}
                to={tool.path}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '20px',
                  background: '#ffffff',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                className="related-tool-link-card"
              >
                <h3
                  style={{
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    margin: 0,
                  }}
                >
                  {tool.title}
                </h3>
                <p
                  style={{
                    fontSize: '0.82rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {tool.description}
                </p>
                <span
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: 'var(--primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: 'auto',
                  }}
                >
                  Try Now <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Styles */}
      <style>{`
        details[open] .faq-chevron-icon {
          transform: rotate(90deg);
        }
        details summary::-webkit-details-marker {
          display: none;
        }
        .related-tool-link-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px -8px rgba(5, 117, 251, 0.15);
          border-color: var(--primary) !important;
        }
      `}</style>
    </>
  );
};

export default ToolPageExtras;
